import React, { useEffect, useRef, useState } from "react";
import "../Login/LoginShop.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BACKEND_URL } from "../../services/Helper";
import { useGoogleLogin } from "@react-oauth/google";
import { auth } from "../../firebase";
import { signInWithPhoneNumber, RecaptchaVerifier } from "firebase/auth";

const notifyB = (msg) => toast.success(msg);
const notifyA = (msg) => toast.error(msg);

const UserLogin = () => {
    const navigate = useNavigate();
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [timer, setTimer] = useState(0);
    const confirmationRef = useRef(null);

    useEffect(() => {
        if (timer <= 0) return;
        const interval = setInterval(() => setTimer((t) => t - 1), 1000);
        return () => clearInterval(interval);
    }, [timer]);

    const setupRecaptcha = () => {
        if (window.recaptchaVerifier) {
            try { window.recaptchaVerifier.clear(); } catch (_) {}
            window.recaptchaVerifier = null;
        }
        window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
            size: "invisible",
        });
    };

    const UserGetOtp = async (e) => {
        e.preventDefault();
        if (!phone || phone.length !== 10) {
            notifyA("Please enter a valid 10-digit phone number");
            return;
        }
        const formattedPhone = phone.startsWith("+91") ? phone : `+91${phone}`;
        try {
            setLoading(true);
            setupRecaptcha();
            const confirmation = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
            confirmationRef.current = confirmation;
            setOtpSent(true);
            setTimer(60);
            notifyB("OTP sent to your phone");
        } catch (error) {
            console.error(error);
            setupRecaptcha();
            notifyA(error.message || "Failed to send OTP. Check phone number and try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!phone || phone.length !== 10) return;
        const formattedPhone = phone.startsWith("+91") ? phone : `+91${phone}`;
        try {
            setLoading(true);
            setupRecaptcha();
            const confirmation = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
            confirmationRef.current = confirmation;
            setTimer(60);
            notifyB("OTP resent");
        } catch (error) {
            console.error(error);
            setupRecaptcha();
            notifyA("Failed to resend OTP");
        } finally {
            setLoading(false);
        }
    };

    const UserhandleLogin = async (e) => {
        e.preventDefault();
        if (!otp || otp.length !== 6) {
            notifyA("OTP should be 6 digits");
            return;
        }
        try {
            setLoading(true);
            const result = await confirmationRef.current.confirm(otp);
            const idToken = await result.user.getIdToken();

            const res = await fetch(`${BACKEND_URL}/nearbe/verifyFirebaseToken`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ idToken }),
            });
            const data = await res.json();
            if (!res.ok) return notifyA(data.error || "Login failed");
            localStorage.setItem("nearbeusertoken", data.usertoken);
            notifyB("Login Successful");
            navigate("/");
        } catch (error) {
            console.error(error);
            notifyA("Invalid OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (codeResponse) => {
        try {
            setLoading(true);
            const res = await fetch(`${BACKEND_URL}/nearbe/usergooglelogin`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    code: codeResponse.code,
                    redirectUri: 'postmessage',
                }),
            });
            const data = await res.json();
            if (!res.ok) return notifyA(data.error || "Google login failed");
            localStorage.setItem("nearbeusertoken", data.usertoken);
            notifyB("Login Successful");
            navigate("/");
        } catch (error) {
            console.error(error);
            notifyA("Google login failed");
        } finally {
            setLoading(false);
        }
    };

    const googleLogin = useGoogleLogin({
        onSuccess: handleGoogleSuccess,
        onError: () => notifyA("Google login failed"),
        flow: "auth-code",
    });

    return (
        <div className="loginshop-container">
            <div id="recaptcha-container"></div>
            <form
                className="loginshop-form"
                onSubmit={otpSent ? UserhandleLogin : UserGetOtp}
            >
                <h2>User Login</h2>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 8, textAlign: 'center' }}>
                    New? We'll create your account automatically.
                </p>

                <input
                    type="number"
                    placeholder="Enter your Phone Number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                />

                {otpSent && (
                    <>
                        <input
                            type="text"
                            placeholder="Enter OTP"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            required
                        />
                        <div className="otp-timer">
                            {timer > 0 ? (
                                <span>Resend OTP in {timer}s</span>
                            ) : (
                                <button
                                    type="button"
                                    className="resend-btn"
                                    disabled={loading}
                                    onClick={handleResend}
                                >
                                    Resend OTP
                                </button>
                            )}
                        </div>
                    </>
                )}

                <button type="submit" className="loginshop-btn" disabled={loading}>
                    {loading ? (
                        <span className="spinner">
                            <div className="spinner-circle"></div>
                            {otpSent ? "Logging in..." : "Sending OTP..."}
                        </span>
                    ) : otpSent ? (
                        "Login"
                    ) : (
                        "Get OTP"
                    )}
                </button>

                <div className="google-divider">
                    <span>or</span>
                </div>

                <button
                    type="button"
                    className="google-btn"
                    onClick={() => googleLogin()}
                    disabled={loading}
                >
                    <img
                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                        alt="Google"
                        style={{ width: 20, height: 20 }}
                    />
                    Continue with Google
                </button>
            </form>
        </div>
    );
};

export default UserLogin;
